import katex from "katex";
import "katex/dist/katex.min.css";

const SUPER = {
  0: "⁰",
  1: "¹",
  2: "²",
  3: "³",
  4: "⁴",
  5: "⁵",
  6: "⁶",
  7: "⁷",
  8: "⁸",
  9: "⁹",
  "+": "⁺",
  "-": "⁻",
  "=": "⁼",
  "(": "⁽",
  ")": "⁾",
  n: "ⁿ",
  i: "ⁱ",
};

const SUB = {
  0: "₀",
  1: "₁",
  2: "₂",
  3: "₃",
  4: "₄",
  5: "₅",
  6: "₆",
  7: "₇",
  8: "₈",
  9: "₉",
  "+": "₊",
  "-": "₋",
  "=": "₌",
  "(": "₍",
  ")": "₎",
};

const LATEX_SYMBOLS = [
  [/\\times\b/g, "×"],
  [/\\cdot\b/g, "·"],
  [/\\div\b/g, "÷"],
  [/\\pm\b/g, "±"],
  [/\\mp\b/g, "∓"],
  [/\\leq\b|\\le\b/g, "≤"],
  [/\\geq\b|\\ge\b/g, "≥"],
  [/\\neq\b|\\ne\b/g, "≠"],
  [/\\approx\b/g, "≈"],
  [/\\equiv\b/g, "≡"],
  [/\\infty\b/g, "∞"],
  [/\\degree\b|\\deg\b/g, "°"],
  [/\\angle\b/g, "∠"],
  [/\\pi\b/g, "π"],
  [/\\theta\b/g, "θ"],
  [/\\alpha\b/g, "α"],
  [/\\beta\b/g, "β"],
  [/\\gamma\b/g, "γ"],
  [/\\delta\b/g, "δ"],
  [/\\Delta\b/g, "Δ"],
  [/\\lambda\b/g, "λ"],
  [/\\mu\b/g, "μ"],
  [/\\sigma\b/g, "σ"],
  [/\\omega\b/g, "ω"],
  [/\\phi\b/g, "φ"],
  [/\\Phi\b/g, "Φ"],
  [/\\psi\b/g, "ψ"],
  [/\\rho\b/g, "ρ"],
  [/\\tau\b/g, "τ"],
  [/\\epsilon\b|\\varepsilon\b/g, "ε"],
  [/\\left\b|\\right\b/g, ""],
];

const WORD_SYMBOLS = [
  [/\binfinity\b/gi, "∞"],
  [/\binfty\b/gi, "∞"],
  [/\btheta\b/gi, "θ"],
  [/\balpha\b/gi, "α"],
  [/\bbeta\b/gi, "β"],
  [/\bgamma\b/gi, "γ"],
  [/\bdelta\b/gi, "δ"],
  [/\blambda\b/gi, "λ"],
  [/\bsigma\b/gi, "σ"],
  [/\bomega\b/gi, "ω"],
  [/\bpi\b/gi, "π"],
];

function mapChars(value, table) {
  return String(value)
    .split("")
    .map((ch) => table[ch] ?? ch)
    .join("");
}

function replaceUntilStable(text, pattern, replacer) {
  let prev;
  do {
    prev = text;
    text = text.replace(pattern, replacer);
  } while (text !== prev);
  return text;
}

function prettyRoots(text) {
  text = replaceUntilStable(text, /\\sqrt\[3\]\{([^{}]*)\}/g, "∛($1)");
  text = replaceUntilStable(text, /\\sqrt\[4\]\{([^{}]*)\}/g, "∜($1)");
  text = replaceUntilStable(text, /\\sqrt\[([^{}\]]+)\]\{([^{}]*)\}/g, (_, n, inner) => {
    const index = mapChars(n, SUPER);
    return `${index}√(${inner})`;
  });
  text = replaceUntilStable(text, /\\sqrt\{([^{}]*)\}/g, "√($1)");
  text = text.replace(/\\sqrt\s*([A-Za-z0-9.]+)/g, "√($1)");
  text = replaceUntilStable(text, /\\cbrt\{([^{}]*)\}/g, "∛($1)");

  text = text.replace(/\bcubic\s*root\s*(?:of\s*)?\(([^)]+)\)/gi, "∛($1)");
  text = text.replace(/\bcubic\s*root\s+of\s+([A-Za-z0-9.]+)/gi, "∛($1)");
  text = text.replace(/\bcbrt\s*\(([^)]+)\)/gi, "∛($1)");
  text = text.replace(/\bfourth\s*root\s*(?:of\s*)?\(([^)]+)\)/gi, "∜($1)");
  text = text.replace(/\bsquare\s*root\s*(?:of\s*)?\(([^)]+)\)/gi, "√($1)");
  text = text.replace(/\bsquare\s*root\s+of\s+([A-Za-z0-9.]+)/gi, "√($1)");
  text = text.replace(/\bsqrt\s*\(([^)]+)\)/gi, "√($1)");
  text = text.replace(/\bsqrt\b/gi, "√");
  return text;
}

function powerPhrase(exp) {
  const inner = String(exp ?? "").replace(/\\circ\b/g, "").trim();
  if (!inner) return "";
  if (/^[0-9+\-()A-Za-z]+$/.test(inner)) return mapChars(inner, SUPER);
  const compact = inner.replace(/[^0-9A-Za-z+\-()]/g, "");
  return compact ? mapChars(compact, SUPER) : inner;
}

function replaceCaretsWithPower(text) {
  let next = String(text ?? "");
  next = next.replace(/\^\{\\circ\}/g, "°");
  next = next.replace(/\^\\circ\b/g, "°");
  next = next.replace(/\^\{([^{}]+)\}/g, (_, inner) => powerPhrase(inner));
  next = next.replace(/\^([A-Za-z0-9+\-()]+)/g, (_, inner) => powerPhrase(inner));
  next = next.replace(/\^/g, "");
  return next.replace(/[ \t]{2,}/g, " ");
}

function prettyPowers(text) {
  text = replaceCaretsWithPower(text);
  text = text.replace(/\*\*\{([^{}]+)\}/g, (_, inner) => powerPhrase(inner));
  text = text.replace(/\*\*([A-Za-z0-9+\-()]+)/g, (_, inner) => powerPhrase(inner));
  text = text.replace(/_\{([^{}]+)\}/g, (_, inner) => mapChars(inner, SUB));
  text = text.replace(/_(\d+)/g, (_, inner) => mapChars(inner, SUB));
  return text;
}

const VULGAR = {
  "1/2": "½",
  "1/3": "⅓",
  "2/3": "⅔",
  "1/4": "¼",
  "3/4": "¾",
  "1/5": "⅕",
  "2/5": "⅖",
  "3/5": "⅗",
  "4/5": "⅘",
  "1/6": "⅙",
  "5/6": "⅚",
  "1/7": "⅐",
  "1/8": "⅛",
  "3/8": "⅜",
  "5/8": "⅝",
  "7/8": "⅞",
  "1/9": "⅑",
  "1/10": "⅒",
};

function toPrettyFraction(numerator, denominator) {
  const num = String(numerator ?? "").trim();
  const den = String(denominator ?? "").trim();
  if (!num || !den) return "";
  const vulgar = VULGAR[`${num}/${den}`];
  if (vulgar) return vulgar;
  if (/^-?\d+$/.test(num) && /^-?\d+$/.test(den)) {
    return `${mapChars(num, SUPER)}⁄${mapChars(den, SUB)}`;
  }
  const wrap = (part) =>
    /^[A-Za-z0-9πθΔαβγδλμσωφψρε]$/.test(part) ? part : `(${part})`;
  return `${wrap(num)}⁄${wrap(den)}`;
}

function prettyFractions(text) {
  text = replaceUntilStable(
    text,
    /\\(?:d|t|c)?frac\{([^{}]*)\}\{([^{}]*)\}/g,
    (_, num, den) => toPrettyFraction(num, den)
  );
  text = text.replace(
    /(?<![\w./])(-?\d{1,2})\s*\/\s*(\d{1,2})(?![\w./])/g,
    (_, num, den) => toPrettyFraction(num, den)
  );
  return text;
}

/**
 * Convert LLM / LaTeX-ish math tokens into unicode symbols for display and PDFs.
 */
export function prettyMathText(value) {
  let text = String(value ?? "");
  if (!text) return "";

  LATEX_SYMBOLS.forEach(([pattern, symbol]) => {
    text = text.replace(pattern, symbol);
  });

  text = replaceUntilStable(text, /\\binom\{([^{}]*)\}\{([^{}]*)\}/g, "C($1, $2)");
  text = prettyRoots(text);
  text = prettyPowers(text);

  WORD_SYMBOLS.forEach(([pattern, symbol]) => {
    text = text.replace(pattern, symbol);
  });

  text = prettyFractions(text);

  text = text.replace(/(\d+(?:\.\d+)?)\s*(?:degrees?|deg)\b/gi, "$1°");
  text = text.replace(/<=/g, "≤");
  text = text.replace(/>=/g, "≥");
  text = text.replace(/!=/g, "≠");
  text = text.replace(/\+\/-/g, "±");
  text = text.replace(/(\d+(?:\.\d+)?)\s*\*\s*/g, "$1 × ");
  text = text.replace(/(\d)\s*x\s*(\d)/gi, "$1 × $2");
  text = text.replace(/(\d)x(\d)/gi, "$1 × $2");

  text = text.replace(/\\[a-zA-Z]+/g, "");
  text = text.replace(/[{}]/g, "");
  text = text.replace(/[–—]/g, "-");
  text = text.replace(/[‘’]/g, "'");
  text = text.replace(/[“”]/g, '"');
  return text.replace(/[ \t]+\n/g, "\n").replace(/[ \t]{2,}/g, " ").trim();
}

const SUPER_TO_ASCII = Object.fromEntries(
  Object.entries(SUPER).map(([ascii, glyph]) => [glyph, String(ascii)])
);

const VULGAR_TO_LATEX = {
  "½": "\\frac{1}{2}",
  "⅓": "\\frac{1}{3}",
  "⅔": "\\frac{2}{3}",
  "¼": "\\frac{1}{4}",
  "¾": "\\frac{3}{4}",
  "⅕": "\\frac{1}{5}",
  "⅖": "\\frac{2}{5}",
  "⅗": "\\frac{3}{5}",
  "⅘": "\\frac{4}{5}",
  "⅙": "\\frac{1}{6}",
  "⅚": "\\frac{5}{6}",
  "⅐": "\\frac{1}{7}",
  "⅛": "\\frac{1}{8}",
  "⅜": "\\frac{3}{8}",
  "⅝": "\\frac{5}{8}",
  "⅞": "\\frac{7}{8}",
  "⅑": "\\frac{1}{9}",
  "⅒": "\\frac{1}{10}",
};

function isInsideDollars(text, offset) {
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < offset; i += 1) {
    if (text[i] === "\\" && (text[i + 1] === "$" || text[i + 1] === "\\")) {
      i += 1;
      continue;
    }
    if (text[i] === "$" && text[i + 1] === "$") {
      inDouble = !inDouble;
      i += 1;
      continue;
    }
    if (text[i] === "$" && !inDouble) inSingle = !inSingle;
  }
  return inSingle || inDouble;
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Turn informal LLM math into LaTeX, leaving existing TeX commands intact. */
export function normalizeLlmMath(value) {
  let text = String(value ?? "");
  if (!text) return "";

  Object.entries(VULGAR_TO_LATEX).forEach(([glyph, latex]) => {
    text = text.replaceAll(glyph, latex);
  });
  text = text.replace(/π/g, "\\pi");
  text = text.replace(/∞/g, "\\infty");
  text = text.replace(/θ/g, "\\theta");
  text = text.replace(/α/g, "\\alpha");
  text = text.replace(/β/g, "\\beta");
  text = text.replace(/γ/g, "\\gamma");
  text = text.replace(/δ/g, "\\delta");
  text = text.replace(/Δ/g, "\\Delta");
  text = text.replace(/λ/g, "\\lambda");
  text = text.replace(/μ/g, "\\mu");
  text = text.replace(/σ/g, "\\sigma");
  text = text.replace(/ω/g, "\\omega");
  text = text.replace(/×/g, "\\times");
  text = text.replace(/÷/g, "\\div");
  text = text.replace(/±/g, "\\pm");
  text = text.replace(/≤/g, "\\leq");
  text = text.replace(/≥/g, "\\geq");
  text = text.replace(/≠/g, "\\neq");
  text = text.replace(/≈/g, "\\approx");
  text = text.replace(/°/g, "^{\\circ}");
  text = text.replace(/∛\(([^)]+)\)/g, "\\sqrt[3]{$1}");
  text = text.replace(/∜\(([^)]+)\)/g, "\\sqrt[4]{$1}");
  text = text.replace(/√\(([^)]+)\)/g, "\\sqrt{$1}");
  text = text.replace(/√([A-Za-z0-9.]+)/g, "\\sqrt{$1}");
  text = text.replace(/√/g, "\\sqrt");
  text = text.replace(/⁄/g, "/");
  text = text.replace(/([A-Za-z0-9)])([⁰¹²³⁴⁵⁶⁷⁸⁹⁻⁺]+)/g, (_, base, sup) => {
    return `${base}^{${mapChars(sup, SUPER_TO_ASCII)}}`;
  });

  text = text.replace(/\bsqrt\s*\(([^)]+)\)/gi, "\\sqrt{$1}");
  text = text.replace(/\bcbrt\s*\(([^)]+)\)/gi, "\\sqrt[3]{$1}");
  text = text.replace(/\bcubic\s*root\s+of\s+([A-Za-z0-9.]+)/gi, "\\sqrt[3]{$1}");
  text = text.replace(/\bsquare\s*root\s+of\s+([A-Za-z0-9.]+)/gi, "\\sqrt{$1}");
  text = text.replace(/(?<!\\)\bpi\b/gi, "\\pi");
  text = text.replace(/(?<!\\)\btheta\b/gi, "\\theta");
  text = text.replace(/(?<!\\)\binfty\b/gi, "\\infty");
  text = text.replace(/(?<!\\)\binfinity\b/gi, "\\infty");
  text = text.replace(/\*\*/g, "^");
  text = text.replace(/<=/g, "\\leq");
  text = text.replace(/>=/g, "\\geq");
  text = text.replace(/!=/g, "\\neq");
  text = text.replace(/\+\/-/g, "\\pm");
  text = text.replace(/(\d+(?:\.\d+)?)\s*\*\s*/g, "$1 \\times ");
  text = text.replace(
    /(?<![\w./\\])(-?\d{1,2})\s*\/\s*(\d{1,2})(?![\w./])/g,
    "\\frac{$1}{$2}"
  );
  return text;
}

export function wrapMathTokens(value) {
  let text = String(value ?? "");
  text = text.replace(/\\[a-zA-Z]+(?:\[[^\]]*\])?(?:\{[^{}]*\})*/g, (match, offset) =>
    isInsideDollars(text, offset) ? match : `$${match}$`
  );
  text = text.replace(
    /(\([^)]+\)|[A-Za-z][A-Za-z0-9]*|\d+(?:\.\d+)?)(?:\^\{[^{}]+\}|\^[A-Za-z0-9]+)+/g,
    (match, _base, offset) => (isInsideDollars(text, offset) ? match : `$${match}$`)
  );
  text = text.replace(/([A-Za-z]|\d+)_(?:\{[^}]+\}|\d+)/g, (match, _base, offset) =>
    isInsideDollars(text, offset) ? match : `$${match}$`
  );
  return text;
}

export function renderMixedMathHtml(value, { displayMode = false } = {}) {
  const prepared = wrapMathTokens(normalizeLlmMath(value));
  if (!prepared) return "";

  const token =
    /\$\$([\s\S]+?)\$\$|\$([^$\n]+?)\$|\\\[([\s\S]+?)\\\]|\\\(([\s\S]+?)\\\)/g;
  let html = "";
  let last = 0;
  let match = token.exec(prepared);
  while (match) {
    if (match.index > last) {
      html += escapeHtml(prepared.slice(last, match.index)).replace(/\n/g, "<br/>");
    }
    const latex = (match[1] ?? match[2] ?? match[3] ?? match[4] ?? "").trim();
    const display = Boolean(match[1] || match[3]) || displayMode;
    try {
      html += katex.renderToString(latex, {
        throwOnError: false,
        displayMode: display,
        output: "html",
      });
    } catch {
      html += escapeHtml(latex);
    }
    last = match.index + match[0].length;
    match = token.exec(prepared);
  }
  if (last < prepared.length) {
    html += escapeHtml(prepared.slice(last)).replace(/\n/g, "<br/>");
  }
  return html;
}
